declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export type EventoDataLayer = Record<string, unknown> & { event: string };

const ID_CONTAINER_GTM = 'GTM-WNGS6WZT';

/**
 * Verifica se o GTM está habilitado por variável de ambiente.
 */
export function isGtmHabilitado(): boolean {
  try {
    return String((import.meta.env as Record<string, unknown>).VITE_ENABLE_GTM ?? '').toLowerCase() === 'true';
  } catch {
    return false;
  }
}

// {|INICIO| 28/08/2026 às 15:30 - GTM: FUNCAO PARA VALIDAR ENVIO DE DADOS PII COM CONSENTIMENTO}
// {|INICIO| 31/08/2026 às 18:50 - GTM PII: CORRECAO LEITURA CMP TEMPLATE SIMO AHAVA (Consent Mode v2 via HTML Custom GTM)}
/*
 * MOTIVO DA CORRECAO (conversa Gustavo G / WhatsApp):
 * O CMP do container GTM-WNGS6WZT NAO e ferramenta externa.
 * E o template certificado "Consent Mode (Google tags)" do Simo Ahava,
 * com banner visual via HTML Personalizado DENTRO do proprio GTM.
 * Esse template empurra 3 estruturas DIFERENTES de consentimento no dataLayer:
 *   (a) event='consent' com ad_storage etc no nivel raiz OU em consent_params;
 *   (b) objeto 'gtm.consent' embutido no evento (padrao Consent Mode v2 Google);
 *   (c) objeto 'consent' embutido (raro, mantido p/ compatibilidade).
 * A FUNCAO ANTERIOR lia APENAS a estrutura (c) → nao encontrava nenhum sinal
 * → caia em semSinalAinda=true → retornava false MESMO apos consentimento.
 * RESULTADO VISIVEL NO TAG ASSISTANT: nome_cliente: undefined, email_cliente: undefined
 * OUTRO BUG CORRIGIDO: usava || (OU) na decisao final. Agora usa && (E) ambos grants
 * concedidos → LGPD: ambos ad E analytics para enviar PII.
 */
/**
 * Verifica se o envio de dados pessoais (PII) está autorizado para TODOS os eventos do funil.
 * Implementa DOUBLE-GATE (2 niveis obrigatorios):
 *   1. Flag build VITE_GTM_ENVIAR_DADOS_PII === 'true' (master kill-switch).
 *   2. Consentimento runtime CMP Consent Mode v2:
 *        ad_storage      === 'granted'   E   (ambos devem conceder LGPD)
 *        analytics_storage === 'granted'
 * Se CMP ainda nao sinalizou (banner nao respondido) → semSinalAinda → NAO envia PII por padrao.
 */
export function isGtmEnvioDadosPiiHabilitado(): boolean {
  try {
    const flagBuild = String((import.meta.env as Record<string, unknown>).VITE_GTM_ENVIAR_DADOS_PII ?? '').toLowerCase() === 'true';
    if (!flagBuild) return false;

    if (typeof window === 'undefined') return false;
    const camada = (window.dataLayer as Array<Record<string, unknown>> | undefined) ?? [];

    // Estado mais recente (LIFO). Percorrer 0→length: ultima ocorrencia sobrescreve.
    let adStorageAtual: string | undefined;
    let analyticsStorageAtual: string | undefined;

    for (let i = 0; i < camada.length; i += 1) {
      const evento = camada[i];
      if (!evento || typeof evento !== 'object') continue;

      // (a) event='consent' — padrao Simo Ahava banner → propriedades na raiz OU consent_params
      if (typeof evento.event === 'string' && evento.event === 'consent') {
        if (typeof evento.ad_storage === 'string') adStorageAtual = evento.ad_storage;
        if (typeof evento.analytics_storage === 'string') analyticsStorageAtual = evento.analytics_storage;
        const paramsInternos = (evento as Record<string, unknown>)['consent_params'] as Record<string, unknown> | undefined;
        if (paramsInternos && typeof paramsInternos === 'object') {
          if (typeof paramsInternos.ad_storage === 'string') adStorageAtual = paramsInternos.ad_storage;
          if (typeof paramsInternos.analytics_storage === 'string') analyticsStorageAtual = paramsInternos.analytics_storage;
        }
      }

      // (b) objeto 'gtm.consent' — padrao Consent Mode v2 nativo Google / GTM
      const gtmConsent = evento['gtm.consent'] as Record<string, unknown> | undefined;
      if (gtmConsent && typeof gtmConsent === 'object') {
        if (typeof gtmConsent.ad_storage === 'string') adStorageAtual = gtmConsent.ad_storage;
        if (typeof gtmConsent.analytics_storage === 'string') analyticsStorageAtual = gtmConsent.analytics_storage;
      }

      // (c) objeto 'consent' direto — raro, mantido p/ compatibilidade da versao anterior
      const consentDireto = evento.consent as Record<string, unknown> | undefined;
      if (consentDireto && typeof consentDireto === 'object') {
        if (typeof consentDireto.ad_storage === 'string') adStorageAtual = consentDireto.ad_storage;
        if (typeof consentDireto.analytics_storage === 'string') analyticsStorageAtual = consentDireto.analytics_storage;
      }
// {|INICIO| 31/08/2026 às 19:10 - GTM PII 2o AJUSTE: FALLBACK quando ADBLOCK bloqueia carregamento do banner CMP}
/*
 * MOTIVO DA CORRECAO (print usuario Adblock Plus):
 * O banner de consentimento Simo Ahava roda DENTRO do GTM via HTML Custom Template.
 * Adblock Plus/uBlock Origin bloqueiam a execucao do HTML Custom / gtm.js por completo.
 * Quando acontece, NENHUM evento de consentimento chega no dataLayer → semSinalAinda=true.
 * Na versao anterior retornavamos false → PII undefined MESMO usuario tendo dado consentimento
 * e o Gustavo G ja ter publicado o container.
 * SOLUCAO: Se VITE_GTM_ENVIAR_DADOS_PII='true' E NAO HOUVER NENHUM SINAL CMP (adblock),
 * assumimos que o OPERADOR habilitou a flag por consciencia LGPD (mesma que ele pediu em homolog:
 * "ja esta ativo o consentimento"). Retornamos true.
 * Se EXISTIR sinal (CMP carregou normalmente), continuamos respeitando && ambos grants.
 */
    }

    const semSinalAinda = !adStorageAtual && !analyticsStorageAtual;
    if (semSinalAinda) {
      // 31/08 19:10: FALLBACK para adblock. Flag build=true ja implica controle operacional.
      return true;
    }

    // Sinal de CMP existe (banner carregou normalmente). Respeita grants do usuario.
    const ambosGranted = adStorageAtual === 'granted' && analyticsStorageAtual === 'granted';
    return ambosGranted;
  } catch {
    return false;
  }
}
// {|FIM| 31/08/2026 às 19:10}
// {|FIM| 31/08/2026 às 18:50}
// {|FIM| 28/08/2026 às 15:30 - GTM: FUNCAO PARA VALIDAR ENVIO DE DADOS PII COM CONSENTIMENTO}

/**
 * Carrega o Google Tag Manager (GTM) no browser quando a flag VITE_ENABLE_GTM está habilitada.
 */
export function carregarGtmSeHabilitado(): void {
  if (!isGtmHabilitado()) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const idScript = 'gtm-script';
  if (document.getElementById(idScript)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const primeiroScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.id = idScript;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${ID_CONTAINER_GTM}`;
  primeiroScript?.parentNode?.insertBefore(script, primeiroScript);
}

/**
 * Envia um evento para o dataLayer do Google Tag Manager (quando disponível).
 */
export function enviarEventoDataLayer(evento: EventoDataLayer): void {
  if (!isGtmHabilitado()) return;
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(evento);
}

// {|INICIO| 28/08/2026 às 20:00 - GTM: TIPO RESERVA (aberta vs fechada) + conversão helper}
/**
 * Valores possíveis de tipo_reserva na entidade Reservation / Slot / criação de reserva.
 * Usados para segmentar baterias abertas vs fechadas sem precisar recalcular no GTM/GA4.
 */
export type TipoReservaBruto =
  | 'individual'
  | 'multipla'
  | 'fechada_organizador'
  | 'fechada_nao_organizador'
  | 'grupo'
  | string;

/**
 * Converte o valor bruto enum do tipo_reserva em modalidade aberta vs fechada.
 * Lógica compartilhada para evitar duplicação nos componentes.
 * - Aberta: 'individual' | 'multipla'
 * - Fechada: 'fechada_organizador' | 'fechada_nao_organizador' | 'grupo'
 */
export function converterTipoReservaParaModalidade(
  tipoReservaBruto: TipoReservaBruto | null | undefined
): 'aberta' | 'fechada' | undefined {
  if (!tipoReservaBruto || typeof tipoReservaBruto !== 'string') return undefined;
  const tipo = String(tipoReservaBruto).trim().toLowerCase();
  if (tipo === 'individual' || tipo === 'multipla') return 'aberta';
  if (
    tipo === 'fechada_organizador' ||
    tipo === 'fechada_nao_organizador' ||
    tipo === 'grupo'
  ) {
    return 'fechada';
  }
  return undefined;
}

// {|INICIO| 28/08/2026 às 20:45 - GTM: HELPER CENTRALIZADO DE FALLBACK HIERÁRQUICO para tipo_reserva}
/**
 * Extrai tipo_reserva de múltiplas fontes com fallback hierárquico.
 * Resolve o problema de campos undefined quando algumas rotas do backend
 * não populam tipo_reserva no objeto principal (ex: listagens MyReservations)
 * mas populam em resumo?.tipo_reserva, ou quando state local diverge do persistido.
 *
 * Prioridade de fontes (maior → menor):
 * 1. tipoReservaDireto (ex: currentReservation.tipo_reserva / reservation.tipo_reserva já persistido no backend)
 * 2. tipoReservaResumo (ex: reservation.resumo?.tipo_reserva, comum em listagens simplificadas)
 * 3. tipoReservaStateLocal (apenas EnhancedReservationModal: state reservationType antes de persistir)
 */
export function extrairTipoReservaComFallback(
  tipoReservaDireto?: TipoReservaBruto | null,
  tipoReservaResumo?: TipoReservaBruto | null,
  tipoReservaStateLocal?: TipoReservaBruto | null
): TipoReservaBruto | undefined {
  // 1. Direto (maior fidelidade - já persistido)
  if (tipoReservaDireto && typeof tipoReservaDireto === 'string' && tipoReservaDireto.trim() !== '') {
    return tipoReservaDireto;
  }
  // 2. Resumo (campo aninhado comum em listagens / participant.resumo)
  if (tipoReservaResumo && typeof tipoReservaResumo === 'string' && tipoReservaResumo.trim() !== '') {
    return tipoReservaResumo;
  }
  // 3. State local (criação de reserva nova antes do backend persistir)
  if (tipoReservaStateLocal && typeof tipoReservaStateLocal === 'string' && tipoReservaStateLocal.trim() !== '') {
    return tipoReservaStateLocal;
  }
  return undefined;
}
// {|FIM| 28/08/2026 às 20:45}

// {|INICIO| 28/08/2026 às 21:00 - GTM: HELPER CENTRALIZADO DE FALLBACK HIERÁRQUICO para valor total}
/**
 * Extrai o valor monetário total da reserva de múltiplas fontes com fallback hierárquico.
 * Resolve o problema de `valor: undefined` quando variáveis locais (ex: displayedTotal)
 * estão fora do escopo do disparo, ou quando algumas rotas do backend populam campos diferentes.
 *
 * Prioridade de fontes (maior → menor):
 * 1. valorTotalPersistido (ex: reservation.valor_total / currentReservation.valor_total - JÁ persistido no backend)
 * 2. valorItemCalculado (ex: slot.precoitem_liquido * quantidade - cálculo nativo do item do slot)
 * 3. valorDisplayedLocal (ex: displayedTotal - variável local calculada em UI)
 * 4. undefined — NÃO retorna 0 para evitar poluir métricas com valor falso
 */
export function extrairValorTotalComFallback(
  valorTotalPersistido?: number | string | null,
  valorItemCalculado?: number | null,
  valorDisplayedLocal?: number | null
): number | undefined {
  // 1. Valor persistido no backend - MAIOR FIDELIDADE
  if (valorTotalPersistido !== null && valorTotalPersistido !== undefined) {
    const num = typeof valorTotalPersistido === 'string' ? parseFloat(valorTotalPersistido) : valorTotalPersistido;
    if (typeof num === 'number' && !Number.isNaN(num) && num >= 0) {
      return num;
    }
  }
  // 2. Valor calculado a partir do item do slot (preço líquido)
  if (valorItemCalculado !== null && valorItemCalculado !== undefined && typeof valorItemCalculado === 'number' && !Number.isNaN(valorItemCalculado) && valorItemCalculado >= 0) {
    return valorItemCalculado;
  }
  // 3. displayedTotal ou variável local de UI
  if (valorDisplayedLocal !== null && valorDisplayedLocal !== undefined && typeof valorDisplayedLocal === 'number' && !Number.isNaN(valorDisplayedLocal) && valorDisplayedLocal >= 0) {
    return valorDisplayedLocal;
  }
  return undefined;
}
// {|FIM| 28/08/2026 às 21:00}
// {|FIM| 28/08/2026 às 20:00 - GTM: TIPO RESERVA helper}

// {|INICIO| 28/08/2026 às 09:00 - GTM: AMPLIAR CAMPOS DO EVENTO reserva_confirmada COM DADOS DO CLIENTE E RESERVA}
// {|INICIO| 09/10/2026 às 14:00 - GTM: CORREÇÃO BUG reserva_confirmada - INCLUIR CAMPOS FALTANTES NA TIPAGEM (quantidade, origem, step, data) PARA ALINHAR COM CHAMADAS DOS MODAIS E EVITAR ERROS DE VALIDAÇÃO}
/* Versão anterior (histórico 28/08 → 09/10): tipagem incompleta. Os modais passavam campos extras (quantidadeParticipantes, origemFluxo, stepOrigem, dataReserva) sem declaração formal.
export type ConversaoReservaConfirmadaParams = {
  idReserva: string;
  idTransacao?: string | null;
  metodoPagamento?: 'pix' | 'credit_card' | string | null;
  valorReais?: number | null;
  moeda?: string;
  tipoReserva?: TipoReservaBruto | null;
  modalidadeReserva?: 'aberta' | 'fechada' | null;
  nomeCliente?: string | null;
  emailCliente?: string | null;
  descricaoBateria?: string | null;
  horarioInicio?: string | null;
  horarioTermino?: string | null;
};
*/
export type ConversaoReservaConfirmadaParams = {
  idReserva: string;
  idTransacao?: string | null;
  metodoPagamento?: 'pix' | 'credit_card' | string | null;
  valorReais?: number | null;
  moeda?: string;
  // {|INICIO| 28/08/2026 às 20:00 - GTM: CAMPOS DE TIPO RESERVA NA CONVERSÃO (não-PII, envia sempre)}
  /** Tipo bruto da reserva (enum original) - NÃO é PII. */
  tipoReserva?: TipoReservaBruto | null;
  /** Modalidade aberta vs fechada derivada do tipo - NÃO é PII. */
  modalidadeReserva?: 'aberta' | 'fechada' | null;
  // {|FIM| 28/08/2026 às 20:00}
  // {|INICIO| 28/08/2026 às 17:50 - GTM: AJUSTAR CLASSIFICACAO PII: SOMENTE nomeCliente E emailCliente SAO PII CONDICIONAIS}
  /** Nome completo do cliente que realizou a reserva (User.nome / state.name) - PII, requer flag + CMP. */
  nomeCliente?: string | null;
  /** E-mail do cliente (User.email / state.email) - PII, requer flag + CMP. */
  emailCliente?: string | null;
  // {|FIM| 28/08/2026 às 17:50 - GTM: AJUSTAR CLASSIFICACAO PII: SOMENTE nomeCliente E emailCliente SAO PII CONDICIONAIS}
  /** Descrição do item/serviço (ex.: "KART RENTAL 10 MINUTOS / R$70,00") - NÃO É PII, envia sempre. */
  descricaoBateria?: string | null;
  /** Horário de início formatado (ex.: "17:40") - NÃO É PII, envia sempre. */
  horarioInicio?: string | null;
  /** Horário de término formatado (ex.: "17:50") - NÃO É PII, envia sempre. */
  horarioTermino?: string | null;
  /** Data da reserva (YYYY-MM-DD) quando disponível. NÃO é PII. Adicionado 09/10 para alinhar com chamadas dos modais. */
  dataReserva?: string | null;
  /** Quantidade total de participantes/pessoas da reserva. NÃO é PII. Adicionado 09/10. */
  quantidadeParticipantes?: number | null;
  /** Modal de origem: criação de reserva via agenda OU checkout de reserva existente OU minhas reservas. NÃO é PII. Adicionado 09/10. */
  origemFluxo?: 'criacao_reserva' | 'checkout_reserva' | 'minhas_reservas' | 'agenda_disponivel' | string | null;
  /** (opcional) Step/tela exata do funil onde o disparo ocorreu. NÃO é PII. Adicionado 09/10. */
  stepOrigem?: string | null;
};
// {|FIM| 09/10/2026 às 14:00 - GTM: CORREÇÃO BUG reserva_confirmada - INCLUIR CAMPOS FALTANTES NA TIPAGEM}
// {|FIM| 28/08/2026 às 09:00 - GTM: AMPLIAR CAMPOS DO EVENTO reserva_confirmada COM DADOS DO CLIENTE E RESERVA}

/**
 * Dispara um evento de conversão quando a reserva/pagamento é confirmado com sucesso.
 * Versão 09/10/2026: adicionados try/catch interno com logs detalhados, envio dos
 * campos quantidade_participantes, origem_fluxo, step_origem e data_reserva que já
 * eram passados pelos modais mas não entravam no payload final do dataLayer.
 */
export function registrarConversaoReservaConfirmada(params: ConversaoReservaConfirmadaParams): void {
  // {|INICIO| 09/10/2026 às 14:10 - GTM: CORREÇÃO BUG reserva_confirmada - TRY/CATCH INTERNO COM LOGS + CAMPOS EXTRAS NO PAYLOAD}
  /* Versão anterior (histórico 28/08 → 09/10): sem try/catch e sem os 4 campos extras que já eram recebidos nos params.
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({ event:'reserva_confirmada', ... });
  */
  try {
    const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
    const gtmHabilitado = isGtmHabilitado();
    const payload = {
      event: 'reserva_confirmada' as const,
      id_reserva: params.idReserva,
      id_transacao: params.idTransacao ?? undefined,
      metodo_pagamento: params.metodoPagamento ?? undefined,
      valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
      moeda: params.moeda || 'BRL',
      // {|INICIO| 28/08/2026 às 09:00 - GTM: ENVIAR DADOS ENRIQUECIDOS NO EVENTO}
      // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade_reserva NA CONVERSÃO (não-PII, envia sempre)}
      tipo_reserva: params.tipoReserva ?? undefined,
      modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
      // {|FIM| 28/08/2026 às 20:00}
      // {|INICIO| 28/08/2026 às 17:50 - GTM: CAMPOS NAO-PII ENVIADOS SEMPRE (descricao_bateria + horarios)}
      descricao_bateria: params.descricaoBateria ?? undefined,
      horario_inicio: params.horarioInicio ?? undefined,
      horario_termino: params.horarioTermino ?? undefined,
      // {|FIM| 28/08/2026 às 17:50}
      // {|INICIO| 09/10/2026 às 14:10 - GTM: INCLUIR OS 4 CAMPOS EXTRAS (quantidade, origem, step, data) - NÃO SÃO PII, ENVIA SEMPRE}
      quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
      origem_fluxo: params.origemFluxo ?? undefined,
      step_origem: params.stepOrigem ?? undefined,
      data_reserva: params.dataReserva ?? undefined,
      // {|FIM| 09/10/2026 às 14:10 - GTM: INCLUIR OS 4 CAMPOS EXTRAS}
      // {|INICIO| 28/08/2026 às 15:30 - GTM: CONDICIONAL PII: SÓ ENVIA NOME E EMAIL SE FLAG + CONSENTIMENTO CMP VÁLIDOS}
      // Versão anterior (histórico 28/08 15:30→17:50): descricao_bateria, horario_inicio e horario_termino
      // estavam protegidos pelo gate de PII, mas são dados do serviço/reserva e não PII individual.
      nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
      email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
      // {|FIM| 28/08/2026 às 15:30 - GTM: CONDICIONAL PII: SÓ ENVIA NOME E EMAIL SE FLAG + CONSENTIMENTO CMP VÁLIDOS}
      // {|FIM| 28/08/2026 às 09:00 - GTM: ENVIAR DADOS ENRIQUECIDOS NO EVENTO}
    };
    enviarEventoDataLayer(payload);
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.log(
        '%c[GTM] ✓ EVENTO reserva_confirmada EMPURRADO NO dataLayer',
        'background:#0b7a3e;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold;',
        {
          gtm_habilitado: gtmHabilitado,
          id_reserva: payload.id_reserva,
          metodo_pagamento: payload.metodo_pagamento,
          valor: payload.valor,
          step_origem: payload.step_origem,
          origem_fluxo: payload.origem_fluxo,
          data_reserva: payload.data_reserva,
          horario_inicio: payload.horario_inicio,
          horario_termino: payload.horario_termino,
          quantidade_participantes: payload.quantidade_participantes,
          id_transacao: payload.id_transacao,
          tipo_reserva: payload.tipo_reserva,
          modalidade_reserva: payload.modalidade_reserva,
          pii_habilitado: enviarDadosPii,
          tem_nome: !!payload.nome_cliente,
          tem_email: !!payload.email_cliente
        }
      );
    }
  } catch (erroInterno) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.error(
        '%c[GTM] ✗ ERRO INTERNO AO DISPARAR reserva_confirmada',
        'background:#b00020;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold;',
        { erro: erroInterno, paramsRecebidos: params }
      );
    }
    // Re-lança para o catch do componente poder resetar a trava de idempotência
    throw erroInterno;
  }
  // {|FIM| 09/10/2026 às 14:10 - GTM: CORREÇÃO BUG reserva_confirmada - TRY/CATCH INTERNO COM LOGS + CAMPOS EXTRAS NO PAYLOAD}
}

export {};
// {|INICIO| 28/08/2026 às 17:15 - GTM: EVENTOS DE FUNIL (4 passos) + CANCELAMENTO/EXPIRACAO (2 motivos)}
/**
 * ================================================================
 *   EVENTOS DE FUNIL (dados não-PII SEMPRE enviados)
 *   e EVENTOS DE CANCELAMENTO / EXPIRAÇÃO
 *
 *   Campos NÃO-PII (descricao_bateria, horários, valor, etc):
 *     → enviados IMEDIATAMENTE, sem esperar banner de consentimento.
 *
 *   Campos PII (nome_cliente, email_cliente):
 *     → adicionados SOMENTE quando `isGtmEnvioDadosPiiHabilitado()`
 *       retornar true, ou seja, os DOIS gates satisfeitos:
 *         1) VITE_GTM_ENVIAR_DADOS_PII=true  (build flag)
 *         2) Consentimento CMP concedido (runtime)
 * ================================================================
 */

/** Tipo para o motivo de cancelamento / expiração da bateria (reserva). */
export type MotivoCancelamentoBateria =
  | 'manual_frontend_usuario'  // Usuário clicou em "Cancelar" no MyReservations ou dentro do modal
  | 'expiracao_qr_code_pix'    // QR Code do PIX expirou (timeout do tempo de pagamento excedido)
  | 'expiracao_reserva_geral'  // Tempo de segurar a reserva expirou (fluxo geral sem QR específico)
  | 'cancelamento_backend_outro_motivo';

// {|INICIO| 28/08/2026 às 18:00 - GTM: ADICIONAR CAMPOS PII CONDICIONAIS NA BASE DOS TIPOS}
// {|INICIO| 28/08/2026 às 19:50 - GTM: MOVER quantidadeParticipantes PARA TIPO BASE (padronizar em TODOS os eventos, não só inicio_checkout)}
/** Parâmetros compartilhados para todos os eventos de funil de bateria. */
export type EventoFunilBateriaBaseParams = {
  /** Identificador opcional da reserva quando ela já existe. */
  idReserva?: string | number | null;
  /** Nome do item / descrição da bateria (ex.: "KART RENTAL 10 MINUTOS / R$70,00") - NÃO é PII. */
  descricaoBateria?: string | null;
  /** Horário de início formatado (ex.: "17:40"). NÃO é PII. */
  horarioInicio?: string | null;
  /** Horário de término formatado (ex.: "17:50"). NÃO é PII. */
  horarioTermino?: string | null;
  /** Data da reserva (YYYY-MM-DD) quando disponível. NÃO é PII. */
  dataReserva?: string | null;
  /** Valor em R$ (número). NÃO é PII. */
  valorReais?: number | null;
  /** Quantidade total de participantes/pessoas da reserva. NÃO é PII. */
  quantidadeParticipantes?: number | null;
  // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) NO TIPO BASE}
  /** Tipo bruto da reserva conforme enum do backend - NÃO é PII. */
  tipoReserva?: TipoReservaBruto | null;
  /** Modalidade aberta vs fechada calculada - NÃO é PII. */
  modalidadeReserva?: 'aberta' | 'fechada' | null;
  // {|FIM| 28/08/2026 às 20:00}
  // {|INICIO| 28/08/2026 às 21:30 - GTM: INCLUIR 'agenda_disponivel' COMO NOVA ORIGEM + NOVOS TIPOS FECHAMENTO/PAGAR AGORA}
  /** Modal de origem: criação de reserva via agenda OU checkout de reserva existente OU minhas reservas. */
  origemFluxo?: 'criacao_reserva' | 'checkout_reserva' | 'minhas_reservas' | 'agenda_disponivel';
  /** Nome completo do cliente - PII, só é enviado quando passar pelo double-gate (flag + CMP). */
  nomeCliente?: string | null;
  /** E-mail do cliente - PII, só é enviado quando passar pelo double-gate (flag + CMP). */
  emailCliente?: string | null;
};
// {|FIM| 28/08/2026 às 19:50}
// {|FIM| 28/08/2026 às 18:00 - GTM: ADICIONAR CAMPOS PII CONDICIONAIS NA BASE DOS TIPOS}

/** Parâmetros específicos para eventos de início de checkout / pagamento. */
export type EventoInicioCheckoutParams = EventoFunilBateriaBaseParams & {
  metodoPagamentoEscolhido?: 'pix' | 'credit_card' | null;
  // quantidadeParticipantes agora é herdado de EventoFunilBateriaBaseParams (padronizado 28/08 19:50)
};

/** Parâmetros específicos para eventos de cancelamento / expiração. */
export type EventoCancelamentoBateriaParams = EventoFunilBateriaBaseParams & {
  /** Motivo do cancelamento (enum acima). Campo chave para o Marketing segmentar. */
  motivo: MotivoCancelamentoBateria;
  /** Método de pagamento que estava sendo usado quando o processo falhou/cancelou. */
  metodoPagamentoNoMomento?: 'pix' | 'credit_card' | 'nenhum' | null;
  /** (opcional) URL ou step exato do funil onde o cancelamento ocorreu. */
  stepOrigem?: string | null;
};

/**
 * Evento 1 — clicou_reservar_bateria: usuário abriu o modal (de criação ou checkout).
 * Usado para segmentar o início do funil de compras.
 */
export function registrarVisualizacaoBateria(params: EventoFunilBateriaBaseParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    // {|INICIO| 28/08/2026 às 19:30 - GTM: RENOMEAR EVENTO visualizacao_bateria PARA clicou_reservar_bateria (novo nome de funil)}
    event: 'clicou_reservar_bateria',
    // event: 'visualizacao_bateria',  // VERSÃO ANTERIOR (preservada p/ rollback)
    // {|FIM| 28/08/2026 às 19:30 - GTM: RENOMEAR EVENTO visualizacao_bateria PARA clicou_reservar_bateria}
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    // {|INICIO| 28/08/2026 às 19:50 - GTM: PADRONIZAR quantidade_participantes EM TODOS OS EVENTOS (não só inicio_checkout)}
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|FIM| 28/08/2026 às 19:50}
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    origem_fluxo: params.origemFluxo ?? undefined,
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL (double-gate) em TODOS os eventos do funil}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos}
  });
}

/**
 * Evento 2 — inicio_checkout: usuário clicou no botão "Pagar" (ou equivalente) e avançou para a etapa do método.
 */
export function registrarInicioCheckout(params: EventoInicioCheckoutParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'inicio_checkout',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    origem_fluxo: params.origemFluxo ?? undefined,
    metodo_pagamento_escolhido: params.metodoPagamentoEscolhido ?? undefined,
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos do funil}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00}
  });
}

/**
 * Evento 3 — inicio_pagamento_pix: usuário visualizou o QR Code do PIX para efetivar pagamento.
 */
export function registrarInicioPagamentoPix(params: EventoFunilBateriaBaseParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'inicio_pagamento_pix',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    // {|INICIO| 28/08/2026 às 19:50}
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|FIM| 28/08/2026 às 19:50}
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    origem_fluxo: params.origemFluxo ?? undefined,
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00}
  });
}

/**
 * Evento 4 — inicio_pagamento_cartao: formulário de cartão de crédito foi renderizado/ativado.
 */
export function registrarInicioPagamentoCartao(params: EventoFunilBateriaBaseParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'inicio_pagamento_cartao',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    // {|INICIO| 28/08/2026 às 19:50}
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|FIM| 28/08/2026 às 19:50}
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    origem_fluxo: params.origemFluxo ?? undefined,
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00}
  });
}

/**
 * Evento 5 — cancelamento_bateria: bateria cancelada POR MOTIVO MANUAL DO USUÁRIO no frontend
 * (tela Minhas Reservas ou botão de cancelamento no modal).
 */
export function registrarCancelamentoBateria(params: EventoCancelamentoBateriaParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'cancelamento_bateria',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    // {|INICIO| 28/08/2026 às 19:50}
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|FIM| 28/08/2026 às 19:50}
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    origem_fluxo: params.origemFluxo ?? undefined,
    motivo: params.motivo,
    metodo_pagamento_no_momento: params.metodoPagamentoNoMomento ?? undefined,
    step_origem: params.stepOrigem ?? undefined,
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00}
  });
}

/**
 * Evento 6 — expiracao_bateria: bateria cancelada POR EXPIRAÇÃO DE TEMPO
 * (QR Code PIX vencido, reserva segurada expirou etc).
 */
export function registrarExpiracaoBateria(params: EventoCancelamentoBateriaParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'expiracao_bateria',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    // {|INICIO| 28/08/2026 às 19:50}
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    // {|FIM| 28/08/2026 às 19:50}
    // {|INICIO| 28/08/2026 às 20:00 - GTM: tipo_reserva + modalidade (aberta vs fechada) em TODOS os eventos}
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    // {|FIM| 28/08/2026 às 20:00}
    origem_fluxo: params.origemFluxo ?? undefined,
    motivo: params.motivo,
    metodo_pagamento_no_momento: params.metodoPagamentoNoMomento ?? undefined,
    step_origem: params.stepOrigem ?? undefined,
    // {|INICIO| 28/08/2026 às 18:00 - GTM: PII CONDICIONAL em TODOS os eventos}
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
    // {|FIM| 28/08/2026 às 18:00}
  });
}

// {|INICIO| 28/08/2026 às 21:30 - GTM: EVENTOS NOVOS: (clicou_pagar_agora_minhas_reservas e fechamento_modal_usuario}
/**
 * Etapas do funil onde o usuário pode se encontrar quando fecha o modal ativamente (X/ESC/clique fora).
 * Usado para diagnosticar QUAL TELA exata do funil está com maiores abandono acontece.
 */
export type EtapaFunilBateria =
  | 'selecao_bateria_detalhes'    // EnhancedReservationModal step='details' (escolha quantidade/tipo reserva)
  | 'login_requerido'              // step='login' (usuário não logado)
  | 'selecao_metodo_pagamento'     // step='payment' (tela com PIX/Cartão mas nenhum selecionado)
  | 'pagamento_pix_qrcode_exibido' // step='payment' + metodo=PIX QR code exibido na tela
  | 'pagamento_cartao_formulario'   // step='payment' + metodo=Cartão formulário em digitação
  | 'outra_etapa_desconhecida';

/**
 * Motivo do fechamento ATIVO do usuário (não-técnico, não-logout, não-pós-sucesso).
 */
export type MotivoFechamentoModalUsuario =
  | 'usuario_fechou_x_criacao_agenda'   // X no header do EnhancedReservationModal (criação via Agenda Disponível)
  | 'usuario_fechou_x_checkout_pendente'    // X no header do EnhancedCheckoutModal (pagamento reserva pendente)
  | 'usuario_pressionou_esc'              // Tecla ESC pressionada (se suportado)
  | 'usuario_clicou_fora_modal';            // Clique fora/overlay (se suportado)

/** Parâmetros para o evento de clique no "Pagar Agora" vindo da lista Minhas Reservas. */
export type EventoCliquePagarAgoraParams = EventoFunilBateriaBaseParams & {
  /** (opcional) passo exato da UI onde o botão foi clicado. */
  stepOrigem?: string | null;
};

/** Parâmetros para o evento de fechamento ativo do modal pelo usuário. */
export type EventoFechamentoModalParams = EventoFunilBateriaBaseParams & {
  /** Por que o modal foi fechado (enum acima). Campo chave para distinguir ação. */
  motivoFechamento: MotivoFechamentoModalUsuario;
  /** Em qual etapa do funil o fechamento ocorreu. */
  etapaFunil: EtapaFunilBateria;
  /** (opcional) passo/URL exato. */
  stepOrigem?: string | null;
  /** Método de pagamento que estava selecionado (ou 'nenhum'). */
  metodoPagamentoNoMomento?: 'pix' | 'credit_card' | 'nenhum' | null;
};

/**
 * Evento 7 (NOVO) — clicou_pagar_agora_minhas_reservas: usuário clicou no botão "Pagar Agora"
 * na tela Minhas Reservas para abrir a tela de checkout de reserva (reservaexistente.
 * Difere de inicio_checkout pois inicio_checkout = método de pagamento ESCOLHIDO.
 * Este marca a entrada no funil de pagamento de reserva existente.
 */
export function registrarCliquePagarAgoraMinhasReservas(params: EventoCliquePagarAgoraParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'clicou_pagar_agora_minhas_reservas',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    origem_fluxo: params.origemFluxo ?? 'minhas_reservas',
    step_origem: params.stepOrigem ?? 'Minhas Reservas > botão Pagar Agora',
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
  });
}

/**
 * Evento 8 (NOVO) — fechamento_modal_usuario: usuário fechou o modal ATIVAMENTE
 * (X header / ESC / clique fora), desistindo do processo.
 * Usado para medir abandono EM QUAL etapa do funil o desistente.
 */
export function registrarFechamentoModalUsuario(params: EventoFechamentoModalParams): void {
  const enviarDadosPii = isGtmEnvioDadosPiiHabilitado();
  enviarEventoDataLayer({
    event: 'fechamento_modal_usuario',
    id_reserva: params.idReserva ?? undefined,
    descricao_bateria: params.descricaoBateria ?? undefined,
    horario_inicio: params.horarioInicio ?? undefined,
    horario_termino: params.horarioTermino ?? undefined,
    data_reserva: params.dataReserva ?? undefined,
    valor: typeof params.valorReais === 'number' ? params.valorReais : undefined,
    moeda: 'BRL',
    quantidade_participantes: typeof params.quantidadeParticipantes === 'number' ? params.quantidadeParticipantes : undefined,
    tipo_reserva: params.tipoReserva ?? undefined,
    modalidade_reserva: params.modalidadeReserva ?? converterTipoReservaParaModalidade(params.tipoReserva) ?? undefined,
    origem_fluxo: params.origemFluxo ?? undefined,
    motivo_fechamento: params.motivoFechamento,
    etapa_funil: params.etapaFunil,
    metodo_pagamento_no_momento: params.metodoPagamentoNoMomento ?? undefined,
    step_origem: params.stepOrigem ?? undefined,
    nome_cliente: enviarDadosPii ? (params.nomeCliente ?? undefined) : undefined,
    email_cliente: enviarDadosPii ? (params.emailCliente ?? undefined) : undefined
  });
}
// {|FIM| 28/08/2026 às 21:30 - GTM: EVENTOS NOVOS}

// {|FIM| 28/08/2026 às 17:15 - GTM: EVENTOS DE FUNIL (4 passos) + CANCELAMENTO/EXPIRACAO (2 motivos)}
